/**
 * Bookstore Books — All Bookstore Books tab
 *
 * Shows books scraped from online bookstores. Admin marks each as
 * SCAN or DON'T SCAN and manually enters ISBNs.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, BookCheck, Ban, Undo2, Plus, ExternalLink, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { glassStyles } from '@/lib/design';
import { toast } from 'sonner';

// Table not yet in generated Supabase types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const bookstoreTable = () => (supabase as any).from('bookstore_books');

interface BookstoreBook {
  id: string;
  title: string;
  author: string | null;
  publisher: string | null;
  description: string | null;
  source_url: string;
  source_store: string;
  cover_image_url: string | null;
  isbn: string | null;
  status: 'pending' | 'scan' | 'dont_scan';
  created_at: string;
  updated_at: string;
}

type StatusFilter = 'all' | 'pending' | 'scan' | 'dont_scan';

export function BookstoreBooks() {
  const [books, setBooks] = useState<BookstoreBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('all');
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [editingIsbn, setEditingIsbn] = useState<string | null>(null);
  const [isbnValue, setIsbnValue] = useState('');
  const [scraping, setScraping] = useState(false);
  const [showAddUrl, setShowAddUrl] = useState(false);
  const [bookUrl, setBookUrl] = useState('');

  // ---------------------------------------------------------------------------
  // Load books
  // ---------------------------------------------------------------------------

  const loadBooks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchErr } = await bookstoreTable()
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchErr) throw new Error(fetchErr.message);
      setBooks(data ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load books');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBooks();
  }, [loadBooks]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const handleSetStatus = async (bookId: string, status: 'scan' | 'dont_scan' | 'pending') => {
    setActionInProgress(bookId);
    try {
      const { error: updateErr } = await bookstoreTable()
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', bookId);

      if (updateErr) {
        toast.error(updateErr.message || 'Failed to update status');
        return;
      }

      setBooks(prev => prev.map(b => b.id === bookId ? { ...b, status } : b));
      toast.success(status === 'scan' ? 'Marked for scanning' : status === 'dont_scan' ? 'Marked as don\'t scan' : 'Reset to pending');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleSaveIsbn = async (bookId: string) => {
    const trimmed = isbnValue.trim();
    if (!trimmed) return;

    setActionInProgress(bookId);
    try {
      const { error: updateErr } = await bookstoreTable()
        .update({ isbn: trimmed, updated_at: new Date().toISOString() })
        .eq('id', bookId);

      if (updateErr) {
        toast.error(updateErr.message || 'Failed to save ISBN');
        return;
      }

      setBooks(prev => prev.map(b => b.id === bookId ? { ...b, isbn: trimmed } : b));
      setEditingIsbn(null);
      setIsbnValue('');
      toast.success('ISBN saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save ISBN');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleAddByUrl = async () => {
    const trimmedUrl = bookUrl.trim();
    if (!trimmedUrl || !trimmedUrl.startsWith('http')) {
      toast.error('Please enter a valid URL');
      return;
    }

    setScraping(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        toast.error('Not authenticated');
        return;
      }

      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/scrape-bookstore`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.session.access_token}`,
            apikey: anonKey,
          },
          body: JSON.stringify({ url: trimmedUrl }),
        }
      );

      const json = await res.json();
      if (!res.ok || !json.success) {
        const detail = json.steps?.length ? ` (last step: ${json.steps[json.steps.length - 1]})` : '';
        toast.error(`${json.error || 'Scraping failed'}${detail}`);
        console.error('[scrape] Response:', json);
        return;
      }

      console.log('[scrape] Success:', json);
      toast.success(`Added "${json.book?.title || 'book'}"`);
      setBookUrl('');
      setShowAddUrl(false);
      loadBooks();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Scraping failed');
    } finally {
      setScraping(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Filtering
  // ---------------------------------------------------------------------------

  const filtered = useMemo(() => {
    let list = books;

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(b =>
        b.title.toLowerCase().includes(q) ||
        (b.author && b.author.toLowerCase().includes(q)) ||
        (b.isbn && b.isbn.includes(q))
      );
    }

    if (filterStatus !== 'all') {
      list = list.filter(b => b.status === filterStatus);
    }

    return list;
  }, [books, searchTerm, filterStatus]);

  const stats = useMemo(() => ({
    total: books.length,
    pending: books.filter(b => b.status === 'pending').length,
    scan: books.filter(b => b.status === 'scan').length,
    dontScan: books.filter(b => b.status === 'dont_scan').length,
    withIsbn: books.filter(b => b.status === 'scan' && b.isbn).length,
  }), [books]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#5C2D91] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl p-8 text-center" style={glassStyles.card}>
        <p className="text-[#FF3B30] text-lg font-semibold mb-2">Error Loading Books</p>
        <p className="text-[#8E8E93] text-sm mb-4">{error}</p>
        <button onClick={loadBooks} className="px-4 py-2 rounded-lg bg-[#5C2D91] text-white text-sm font-medium">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-[#1C1C1E]">All Bookstore Books</h2>
          <p className="text-sm text-[#8E8E93] mt-0.5">
            {stats.total} books scraped &middot; {stats.pending} pending &middot; {stats.scan} to scan &middot; {stats.withIsbn} ready for library
          </p>
        </div>
        <button
          onClick={() => setShowAddUrl(v => !v)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#5C2D91] text-white text-sm font-medium transition-colors hover:bg-[#5C2D91]/90 self-start"
        >
          <Plus className="w-4 h-4" />
          Add Book
        </button>
      </div>

      {/* Add by URL panel */}
      {showAddUrl && (
        <div className="rounded-2xl p-4 mb-6" style={glassStyles.card}>
          <p className="text-sm text-[#1C1C1E] font-medium mb-2">Paste a book URL from any bookstore site</p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <input
                type="url"
                value={bookUrl}
                onChange={e => setBookUrl(e.target.value)}
                placeholder="https://www.booksaremagic.net/..."
                className="w-full px-3 py-2.5 bg-white border border-black/[0.08] rounded-xl text-[#1C1C1E] text-sm placeholder-[#8E8E93] focus:outline-none focus:border-[#5C2D91] focus:ring-1 focus:ring-[#5C2D91]/20"
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter') handleAddByUrl();
                  if (e.key === 'Escape') { setShowAddUrl(false); setBookUrl(''); }
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleAddByUrl}
                disabled={scraping || !bookUrl.trim()}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#5C2D91] text-white text-sm font-medium transition-colors hover:bg-[#5C2D91]/90 disabled:opacity-50"
              >
                {scraping ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                {scraping ? 'Scraping...' : 'Scrape'}
              </button>
              <button
                onClick={() => { setShowAddUrl(false); setBookUrl(''); }}
                className="px-3 py-2.5 rounded-xl bg-black/[0.03] text-[#636366] text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center mb-4">
        <div className="relative flex-1 md:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8E8E93]" />
          <input
            type="text"
            placeholder="Search title, author, ISBN..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-black/[0.08] rounded-xl text-[#1C1C1E] text-sm placeholder-[#8E8E93] focus:outline-none focus:border-[#5C2D91] focus:ring-1 focus:ring-[#5C2D91]/20"
          />
        </div>

        <div className="flex gap-1">
          {([
            ['all', 'All'],
            ['pending', 'Pending'],
            ['scan', 'Scan'],
            ['dont_scan', "Don't Scan"],
          ] as const).map(([value, label]) => (
            <button
              key={value}
              onClick={() => setFilterStatus(value)}
              className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                filterStatus === value
                  ? 'bg-[#5C2D91] text-white'
                  : 'bg-black/[0.03] text-[#636366] hover:bg-black/[0.06]'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="rounded-2xl p-8 text-center" style={glassStyles.card}>
          <p className="text-[#8E8E93] text-sm">
            {books.length === 0
              ? 'No books scraped yet. Click "Scrape Now" to fetch books from Books Are Magic.'
              : 'No books match your current filters.'}
          </p>
        </div>
      )}

      {/* Book list */}
      {filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map(book => {
            const isActioning = actionInProgress === book.id;
            const isEditingIsbn = editingIsbn === book.id;

            return (
              <div key={book.id} className="rounded-2xl p-4" style={glassStyles.card}>
                <div className="flex gap-4">
                  {/* Cover image */}
                  {book.cover_image_url && (
                    <img
                      src={book.cover_image_url}
                      alt={book.title}
                      className="w-16 h-24 object-cover rounded-lg shrink-0 bg-black/[0.03]"
                    />
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-[#1C1C1E] truncate">{book.title}</h3>
                        {book.author && (
                          <p className="text-sm text-[#636366]">{book.author}</p>
                        )}
                        {book.publisher && (
                          <p className="text-xs text-[#8E8E93]">{book.publisher}</p>
                        )}
                      </div>

                      {/* Status badge */}
                      <span className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold ${
                        book.status === 'scan'
                          ? 'bg-[#34C759]/15 text-[#34C759]'
                          : book.status === 'dont_scan'
                            ? 'bg-black/[0.05] text-[#8E8E93]'
                            : 'bg-[#FF9F0A]/15 text-[#FF9F0A]'
                      }`}>
                        {book.status === 'scan' ? 'SCAN' : book.status === 'dont_scan' ? "DON'T SCAN" : 'PENDING'}
                      </span>
                    </div>

                    {/* Description */}
                    {book.description && (
                      <p className="text-xs text-[#8E8E93] mt-2 line-clamp-2">{book.description}</p>
                    )}

                    {/* ISBN row */}
                    <div className="flex items-center gap-2 mt-2">
                      {isEditingIsbn ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={isbnValue}
                            onChange={e => setIsbnValue(e.target.value)}
                            placeholder="Enter ISBN..."
                            className="px-3 py-1.5 bg-white border border-black/[0.08] rounded-lg text-[#1C1C1E] text-xs font-mono focus:outline-none focus:border-[#5C2D91] w-40"
                            autoFocus
                            onKeyDown={e => {
                              if (e.key === 'Enter') handleSaveIsbn(book.id);
                              if (e.key === 'Escape') { setEditingIsbn(null); setIsbnValue(''); }
                            }}
                          />
                          <button
                            onClick={() => handleSaveIsbn(book.id)}
                            disabled={!isbnValue.trim() || isActioning}
                            className="px-2 py-1.5 rounded-lg bg-[#5C2D91] text-white text-xs font-medium disabled:opacity-30"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => { setEditingIsbn(null); setIsbnValue(''); }}
                            className="px-2 py-1.5 rounded-lg bg-black/[0.03] text-[#636366] text-xs"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <>
                          {book.isbn ? (
                            <span className="text-xs font-mono text-[#636366]">ISBN: {book.isbn}</span>
                          ) : (
                            <button
                              onClick={() => { setEditingIsbn(book.id); setIsbnValue(book.isbn || ''); }}
                              className="text-xs text-[#5C2D91] hover:underline"
                            >
                              + Add ISBN
                            </button>
                          )}
                        </>
                      )}

                      <a
                        href={book.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-[#5C2D91] hover:underline ml-auto"
                      >
                        <ExternalLink className="w-3 h-3" />
                        View on site
                      </a>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2 mt-3">
                      {book.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleSetStatus(book.id, 'scan')}
                            disabled={isActioning}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#34C759]/15 text-[#34C759] text-xs font-medium hover:bg-[#34C759]/25 disabled:opacity-30"
                          >
                            <BookCheck className="w-3.5 h-3.5" />
                            Scan
                          </button>
                          <button
                            onClick={() => handleSetStatus(book.id, 'dont_scan')}
                            disabled={isActioning}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/[0.03] text-[#8E8E93] text-xs font-medium hover:bg-black/[0.06] disabled:opacity-30"
                          >
                            <Ban className="w-3.5 h-3.5" />
                            Don't Scan
                          </button>
                        </>
                      )}
                      {book.status === 'scan' && (
                        <button
                          onClick={() => handleSetStatus(book.id, 'pending')}
                          disabled={isActioning}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/[0.03] text-[#8E8E93] text-xs font-medium hover:bg-black/[0.06] disabled:opacity-30"
                        >
                          <Undo2 className="w-3.5 h-3.5" />
                          Undo
                        </button>
                      )}
                      {book.status === 'dont_scan' && (
                        <button
                          onClick={() => handleSetStatus(book.id, 'pending')}
                          disabled={isActioning}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/[0.03] text-[#8E8E93] text-xs font-medium hover:bg-black/[0.06] disabled:opacity-30"
                        >
                          <Undo2 className="w-3.5 h-3.5" />
                          Undo
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default BookstoreBooks;
